import { Collapse, Group, Paper, Stack } from '@mantine/core';
import styles from './CollapsibleContactCard.module.css';
import PrimaryContactButton from './PrimaryContactButton';
import CollapseButton from './CollapseButton';
import DeleteButton from './DeleteButton';

export const CollapsibleContactCard = ({
  title,
  accessibilityLabel,
  icon,
  canDelete,
  onDelete,
  canMakePrimary,
  isPrimary,
  onWantsPrimary,
  childrenWhenCollapsed,
  childrenWhenExpanded,
  isCollapsed,
  onToggleCollapse,
}: {
  title: React.ReactNode;
  accessibilityLabel: string;
  icon: React.ReactNode;
  canDelete: boolean;
  onDelete: () => void;
  canMakePrimary: boolean;
  isPrimary: boolean;
  onWantsPrimary: () => void;
  childrenWhenCollapsed?: React.ReactNode;
  childrenWhenExpanded: React.ReactNode;
  isCollapsed: boolean;
  onToggleCollapse: () => void;
}) => {
  const transitionDuration = 200;
  return (
    <Paper
      radius='xl'
      withBorder
      className={`${styles.collapsibleContactCard} ${
        isCollapsed ? styles.collapsed : ''
      }`}
      onClick={() => isCollapsed && onToggleCollapse()}
    >
      <Stack gap='0'>
        <Header
          title={title}
          accessibilityLabel={accessibilityLabel}
          canDelete={canDelete}
          onDelete={onDelete}
          canMakePrimary={canMakePrimary}
          isPrimary={isPrimary}
          onWantsPrimary={onWantsPrimary}
          isCollapsed={isCollapsed}
          onToggleCollapse={onToggleCollapse}
          icon={icon}
          childrenWhenCollapsed={childrenWhenCollapsed}
        />

        <Collapse in={!isCollapsed} transitionDuration={transitionDuration}>
          {childrenWhenExpanded}
        </Collapse>
      </Stack>
    </Paper>
  );
};

const Header = ({
  title,
  accessibilityLabel,
  canDelete,
  onDelete,
  canMakePrimary,
  isPrimary,
  onWantsPrimary,
  isCollapsed,
  onToggleCollapse,
  icon,
  childrenWhenCollapsed,
}: {
  title: React.ReactNode;
  accessibilityLabel: string;
  canDelete: boolean;
  onDelete: () => void;
  canMakePrimary: boolean;
  isPrimary: boolean;
  onWantsPrimary: () => void;
  isCollapsed: boolean;
  onToggleCollapse: () => void;
  icon: React.ReactNode;
  childrenWhenCollapsed?: React.ReactNode;
}) => {
  return (
    <Group
      justify='stretch'
      align='flex-start'
      className={`${styles.header} ${!isCollapsed ? styles.expanded : ''}`}
    >
      <CollapseButton
        isCollapsed={isCollapsed}
        onToggleCollapse={onToggleCollapse}
      />
      <Stack gap='0' flex='1 0 0'>
        <Group justify='space-between' align='center'>
          <Group gap='xs'>
            {icon}
            {title}
            {canMakePrimary && (
              <PrimaryContactButton
                isPrimary={isPrimary}
                onWantsPrimary={onWantsPrimary}
              />
            )}
          </Group>

          {canDelete && (
            <DeleteButton
              label={`Delete ${accessibilityLabel}`}
              onDelete={onDelete}
            />
          )}
        </Group>
        {childrenWhenCollapsed && (
          <Collapse in={isCollapsed} transitionDuration={200}>
            {childrenWhenCollapsed}
          </Collapse>
        )}
      </Stack>
    </Group>
  );
};
